import type { TaskRequest } from '../../api/v1';

/**
 * Where a task was ASKED to go, read from its dispatch request.
 *
 * Separate from `task.phases`, which is RMF's record of places actually
 * VISITED and is therefore empty for a queued task -- the defect this
 * exists to fix. Keeping the two apart is deliberate: they can legitimately
 * disagree (RMF replans, a clean's go-to resolves to a different vertex),
 * and reconciling them would be inventing an answer.
 *
 * PURE, and category-keyed, so the calendar (which holds the same
 * `TaskRequest` on an un-fired occurrence) and the twin can reuse it
 * without touching the drawer.
 *
 * The shapes come from the dispatch form's own `buildPhases` and the
 * worker's builders: station/dustbag/charge are ONE `go_to_place`, patrol
 * is N of them in visit order, and a clean is a `perform_action` carrying
 * `{zone, action: 'clean'}` -- which is why a clean must NOT be read as
 * `phases[0].params.place`.
 */
export type PlannedRole =
  | 'destination'
  | 'pickup'
  | 'dropoff'
  | 'waypoint'
  | 'zone'
  | 'station'
  | 'charger';

export interface PlannedPlace {
  role: PlannedRole;
  place: string;
  /** 1-based, for the ordered roles (patrol). */
  index?: number;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

function goToPlaces(request: TaskRequest): string[] {
  const out: string[] = [];
  for (const phase of request.phases ?? []) {
    if (phase?.kind !== 'go_to_place') continue;
    const place = str(phase.params?.place);
    if (place) out.push(place);
  }
  return out;
}

function cleanZone(request: TaskRequest): string | null {
  for (const phase of request.phases ?? []) {
    if (phase?.kind !== 'perform_action') continue;
    if (phase.params?.action !== 'clean') continue;
    const zone = str(phase.params?.zone);
    if (zone) return zone;
  }
  return null;
}

export function plannedPlaces(
  category: string | undefined,
  request: TaskRequest | undefined | null,
): PlannedPlace[] {
  if (!request) return [];
  const places = goToPlaces(request);

  switch (category) {
    case 'clean': {
      const zone = cleanZone(request);
      if (zone) return [{ role: 'zone', place: zone }];
      // A clean whose zone did not ride on a perform_action is not a
      // shape this platform dispatches; fall through to whatever
      // go_to_place it does have rather than showing nothing.
      break;
    }
    case 'station_docking':
      return places.slice(0, 1).map((place) => ({ role: 'destination', place }));
    case 'charge':
      return places.slice(0, 1).map((place) => ({ role: 'charger', place }));
    case 'dustbag':
      return places.slice(0, 1).map((place) => ({ role: 'station', place }));
    case 'delivery': {
      // Written out rather than indexed into a role array: the project
      // compiles with `noUncheckedIndexedAccess`, and a delivery with one
      // place is a pickup with no dropoff yet, not a hole.
      const out: PlannedPlace[] = [];
      const [pickup, dropoff] = places;
      if (pickup) out.push({ role: 'pickup', place: pickup });
      if (dropoff) out.push({ role: 'dropoff', place: dropoff });
      return out;
    }
    default:
      break;
  }
  // patrol, and anything this build does not know about: every place in
  // visit order, numbered. An unknown category showing its places is more
  // useful than an unknown category showing nothing.
  return places.map((place, i) => ({ role: 'waypoint', place, index: i + 1 }));
}

export function roleLabel(place: PlannedPlace): string {
  switch (place.role) {
    case 'destination':
      return 'Destination';
    case 'pickup':
      return 'Pickup';
    case 'dropoff':
      return 'Dropoff';
    case 'zone':
      return 'Zone';
    case 'station':
      return 'Station';
    case 'charger':
      return 'Charger';
    case 'waypoint':
    default:
      return place.index ? `${place.index}.` : '';
  }
}
